/**
 * Proposal Timber Diff
 * --------------------
 * Pure functions that compare an existing proposal's structures against a
 * freshly parsed timber CSV result and return a structured diff. Designed
 * to back the "Update Timber List from CSV" preview modal.
 *
 * Matching strategy
 * - Structure ↔ CSV group: case-insensitive trimmed `nodeSet.name` vs `parsed.group`.
 * - Line ↔ CSV row (within a matched structure): composite key
 *     `pieceName|normalizedDimensions` (case- and whitespace-normalized).
 *   Duplicate keys are matched positionally within the duplicate bucket so
 *   that, e.g., two "RAFTER 4x8x12" rows on each side line up by index.
 *
 * The functions here are pure and side-effect free; they read state and the
 * parser's result and return a plain object. Apply logic lives in
 * `proposalTimberApply.ts`.
 */

import type { ProposalNodeSet, TimberListItem } from '../types';
import type { CSVParseResult, ParsedTimberItem } from './proposalCSVParser';
import { groupItemsByGroup } from './proposalCSVParser';

/** Minimal shape required by the diff/apply utilities — both `ProposalBuilderData`
 *  and the page-local `ProposalState` satisfy this. */
export interface ProposalLikeWithNodeSets {
  nodeSets: ProposalNodeSet[];
}

// ─── Types ─────────────────────────────────────────────────────────────────────

export type StructureChangeKind = 'updated' | 'added' | 'removedFromCsv' | 'unchanged';
export type LineChangeKind = 'modified' | 'added' | 'removed' | 'unchanged';

/** Subset of TimberListItem fields the CSV directly determines. */
export type CsvDerivedFields = 'pieceName' | 'quantity' | 'dimensions' | 'boardFeet';

export interface FieldDiff {
  field: CsvDerivedFields;
  before: string | number;
  after: string | number;
}

/** Per-line change inside a matched structure. */
export interface LineChange {
  kind: LineChangeKind;
  /** Stable composite key used to match the line; useful for React keys + tests. */
  matchKey: string;
  /** Existing line in the proposal (present when kind is modified/removed/unchanged). */
  before?: TimberListItem;
  /** CSV-derived shape (present when kind is modified/added/unchanged). */
  after?: {
    pieceName: string;
    quantity: number;
    dimensions: string;
    boardFeet: number;
    lineNumber?: number;
    productionNumber?: string;
  };
  fieldDiffs: FieldDiff[];
}

export interface StructureDiff {
  kind: StructureChangeKind;
  /** Existing nodeSet id (present unless kind === 'added'). */
  existingNodeSetId?: string;
  /** Existing nodeSet name (present unless kind === 'added'). */
  existingName?: string;
  /** CSV group name (present unless kind === 'removedFromCsv'). */
  csvGroupName?: string;
  /** Pre-grouped CSV items for this structure (present unless kind === 'removedFromCsv'). */
  csvItems?: ParsedTimberItem[];
  lineChanges: LineChange[];
  totals: {
    bdftBefore: number;
    bdftAfter: number;
    lineBefore: number;
    lineAfter: number;
  };
}

export interface ProposalTimberDiff {
  structureDiffs: StructureDiff[];
  summary: {
    updated: number;
    added: number;
    removedFromCsv: number;
    unchanged: number;
    /** Σ (bdftAfter − bdftBefore) over updated + added structures. */
    totalBdftDelta: number;
  };
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

/** Normalize a dimension string the same way StructureDetailModal does. */
export function normalizeDimensionsForMatch(dim: string): string {
  return (dim || '')
    .toLowerCase()
    .replace(/×/g, 'x')
    .replace(/\s+/g, '')
    .trim();
}

/** Case- and whitespace-insensitive structure name normalization for matching. */
export function normalizeStructureName(name: string): string {
  return (name || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

export function lineKey(pieceName: string, dimensions: string): string {
  return `${(pieceName || '').toLowerCase().trim()}|${normalizeDimensionsForMatch(dimensions)}`;
}

function csvAfterFromParsed(item: ParsedTimberItem): NonNullable<LineChange['after']> {
  return {
    pieceName: item.pieceName,
    quantity: item.quantity,
    dimensions: item.normalizedDimensions,
    boardFeet: item.boardFeet,
    lineNumber: item.lineNumber,
    productionNumber: item.productionNumber,
  };
}

function takeUnconsumed(
  candidates: TimberListItem[] | undefined,
  consumedIds: Set<string>,
): TimberListItem | undefined {
  if (!candidates?.length) return undefined;
  return candidates.find((b) => !consumedIds.has(b.id));
}

/** BDFT contribution of a single line, matching StructureDetailModal's recalc. */
function lineBdft(item: { boardFeet: number; quantity: number }): number {
  return (Number(item.boardFeet) || 0) * (Number(item.quantity) || 0);
}

function structureBdftFromLines(items: Array<{ boardFeet: number; quantity: number }>): number {
  return items.reduce((sum, it) => sum + lineBdft(it), 0);
}

function structureBdftFromCsv(items: ParsedTimberItem[]): number {
  return items.reduce((sum, it) => sum + (it.boardFeet || 0) * (it.quantity || 0), 0);
}

// ─── Per-structure diff ────────────────────────────────────────────────────────

/**
 * Diff a single structure's existing timberList against a CSV group.
 *
 * Ordering contract: the returned `lineChanges` walk the CSV in its original
 * row order, emitting `modified` / `unchanged` / `added` inline at each CSV
 * position. Lines that exist only in the proposal (kind === 'removed') are
 * appended at the end as their own block, since they have no CSV position to
 * anchor to. The Apply step preserves this order verbatim, so after a CSV
 * import the structure's timberList visually mirrors the CSV row-for-row —
 * which is critical for the user's manual verification step.
 *
 * Matching uses `(pieceName, normalizedDimensions)` with positional/FIFO
 * fallback so duplicate keys pair up by occurrence rather than the CSV row
 * always claiming the first match. Exported so callers / tests can use it
 * standalone.
 */
export function diffStructureLines(
  existingList: TimberListItem[],
  csvItems: ParsedTimberItem[],
): { lineChanges: LineChange[]; bdftBefore: number; bdftAfter: number } {
  const beforeByLineNumber = new Map<number, TimberListItem>();
  const beforeByProduction = new Map<string, TimberListItem[]>();
  const beforeBuckets = new Map<string, TimberListItem[]>();

  for (const b of existingList) {
    if (b.sourceLineNumber != null) {
      beforeByLineNumber.set(b.sourceLineNumber, b);
    }
    if (b.sourceProductionNumber) {
      const prodKey = b.sourceProductionNumber.trim();
      const arr = beforeByProduction.get(prodKey) ?? [];
      arr.push(b);
      beforeByProduction.set(prodKey, arr);
    }
    const k = lineKey(b.name, b.dimensions);
    const arr = beforeBuckets.get(k) ?? [];
    arr.push(b);
    beforeBuckets.set(k, arr);
  }

  const lineChanges: LineChange[] = [];
  const consumedIds = new Set<string>();

  const matchExistingForCsv = (a: ParsedTimberItem): TimberListItem | undefined => {
    if (a.lineNumber != null) {
      const byLine = beforeByLineNumber.get(a.lineNumber);
      if (byLine && !consumedIds.has(byLine.id)) return byLine;
    }
    if (a.productionNumber) {
      const byProd = takeUnconsumed(beforeByProduction.get(a.productionNumber.trim()), consumedIds);
      if (byProd) return byProd;
    }
    const k = lineKey(a.pieceName, a.normalizedDimensions);
    return takeUnconsumed(beforeBuckets.get(k), consumedIds);
  };

  for (const a of csvItems) {
    const beforeRaw = matchExistingForCsv(a);
    const k = lineKey(a.pieceName, a.normalizedDimensions);

    if (beforeRaw) {
      consumedIds.add(beforeRaw.id);
      const fieldDiffs: FieldDiff[] = [];

      if ((beforeRaw.name || '') !== a.pieceName) {
        fieldDiffs.push({ field: 'pieceName', before: beforeRaw.name || '', after: a.pieceName });
      }
      if ((Number(beforeRaw.quantity) || 0) !== (Number(a.quantity) || 0)) {
        fieldDiffs.push({ field: 'quantity', before: beforeRaw.quantity, after: a.quantity });
      }
      if (normalizeDimensionsForMatch(beforeRaw.dimensions) !== normalizeDimensionsForMatch(a.normalizedDimensions)) {
        fieldDiffs.push({ field: 'dimensions', before: beforeRaw.dimensions, after: a.normalizedDimensions });
      }
      const beforeBdftPerPiece = Number(beforeRaw.boardFeet) || 0;
      const afterBdftPerPiece = Number(a.boardFeet) || 0;
      if (Math.abs(beforeBdftPerPiece - afterBdftPerPiece) > 0.01) {
        fieldDiffs.push({ field: 'boardFeet', before: beforeBdftPerPiece, after: afterBdftPerPiece });
      }

      lineChanges.push({
        kind: fieldDiffs.length > 0 ? 'modified' : 'unchanged',
        matchKey: k,
        before: beforeRaw,
        after: csvAfterFromParsed(a),
        fieldDiffs,
      });
    } else {
      lineChanges.push({
        kind: 'added',
        matchKey: k,
        after: csvAfterFromParsed(a),
        fieldDiffs: [],
      });
    }
  }

  for (const b of existingList) {
    if (consumedIds.has(b.id)) continue;
    lineChanges.push({
      kind: 'removed',
      matchKey: lineKey(b.name, b.dimensions),
      before: b,
      fieldDiffs: [],
    });
  }

  const bdftBefore = structureBdftFromLines(existingList);
  const bdftAfter = structureBdftFromCsv(csvItems);

  return { lineChanges, bdftBefore, bdftAfter };
}

// ─── Top-level diff ────────────────────────────────────────────────────────────

export function diffProposalTimber(
  state: ProposalLikeWithNodeSets,
  parsed: CSVParseResult,
): ProposalTimberDiff {
  const grouped = groupItemsByGroup(parsed.items); // { groupName: ParsedTimberItem[] }

  // Index existing structures by normalized name. If the user has duplicate
  // structure names (rare but possible) we keep them in a list and pop the
  // first match each time.
  const existingByName = new Map<string, ProposalNodeSet[]>();
  state.nodeSets.forEach(ns => {
    const k = normalizeStructureName(ns.name);
    const arr = existingByName.get(k) ?? [];
    arr.push(ns);
    existingByName.set(k, arr);
  });

  const csvGroupNames = Object.keys(grouped);
  const structureDiffs: StructureDiff[] = [];
  const seenExistingIds = new Set<string>();

  // Pass 1: walk CSV groups, match each to an existing structure if possible.
  for (const groupName of csvGroupNames) {
    const csvItems = grouped[groupName];
    const k = normalizeStructureName(groupName);
    const candidates = existingByName.get(k);
    const existingNs = candidates && candidates.length > 0 ? candidates.shift()! : undefined;

    if (existingNs) {
      seenExistingIds.add(existingNs.id);
      const linesResult = diffStructureLines(existingNs.materials.timberList || [], csvItems);
      const isUnchanged = linesResult.lineChanges.every(lc => lc.kind === 'unchanged');
      structureDiffs.push({
        kind: isUnchanged ? 'unchanged' : 'updated',
        existingNodeSetId: existingNs.id,
        existingName: existingNs.name,
        csvGroupName: groupName,
        csvItems,
        lineChanges: linesResult.lineChanges,
        totals: {
          bdftBefore: linesResult.bdftBefore,
          bdftAfter: linesResult.bdftAfter,
          lineBefore: existingNs.materials.timberList?.length || 0,
          lineAfter: csvItems.length,
        },
      });
    } else {
      // CSV group with no matching existing structure → "added" (new)
      structureDiffs.push({
        kind: 'added',
        csvGroupName: groupName,
        csvItems,
        lineChanges: csvItems.map(a => ({
          kind: 'added' as const,
          matchKey: lineKey(a.pieceName, a.normalizedDimensions),
          after: {
            pieceName: a.pieceName,
            quantity: a.quantity,
            dimensions: a.normalizedDimensions,
            boardFeet: a.boardFeet,
          },
          fieldDiffs: [],
        })),
        totals: {
          bdftBefore: 0,
          bdftAfter: structureBdftFromCsv(csvItems),
          lineBefore: 0,
          lineAfter: csvItems.length,
        },
      });
    }
  }

  // Pass 2: existing structures with timber that the CSV no longer mentions.
  for (const ns of state.nodeSets) {
    if (seenExistingIds.has(ns.id)) continue;
    const list = ns.materials.timberList || [];
    if (list.length === 0) continue; // empty proposal-only structures aren't interesting
    structureDiffs.push({
      kind: 'removedFromCsv',
      existingNodeSetId: ns.id,
      existingName: ns.name,
      lineChanges: list.map(b => ({
        kind: 'removed' as const,
        matchKey: lineKey(b.name, b.dimensions),
        before: b,
        fieldDiffs: [],
      })),
      totals: {
        bdftBefore: structureBdftFromLines(list),
        bdftAfter: 0,
        lineBefore: list.length,
        lineAfter: 0,
      },
    });
  }

  // Sort: updated first, then added, then removedFromCsv, then unchanged. Within
  // each bucket, alphabetical by display name. Keeps the modal predictable.
  const order: Record<StructureChangeKind, number> = {
    updated: 0, added: 1, removedFromCsv: 2, unchanged: 3,
  };
  structureDiffs.sort((a, b) => {
    if (order[a.kind] !== order[b.kind]) return order[a.kind] - order[b.kind];
    const an = (a.existingName ?? a.csvGroupName ?? '').toLowerCase();
    const bn = (b.existingName ?? b.csvGroupName ?? '').toLowerCase();
    return an.localeCompare(bn);
  });

  // Summary
  let updated = 0, added = 0, removedFromCsv = 0, unchanged = 0;
  let totalBdftDelta = 0;
  for (const sd of structureDiffs) {
    switch (sd.kind) {
      case 'updated': updated += 1; totalBdftDelta += (sd.totals.bdftAfter - sd.totals.bdftBefore); break;
      case 'added': added += 1; totalBdftDelta += sd.totals.bdftAfter; break;
      case 'removedFromCsv': removedFromCsv += 1; break; // no delta unless the user opts to delete
      case 'unchanged': unchanged += 1; break;
    }
  }

  return {
    structureDiffs,
    summary: { updated, added, removedFromCsv, unchanged, totalBdftDelta },
  };
}
